import { z } from 'zod';

import { BooleanParamSchema } from '../common/boolean-param/boolean-param';
import { UuidSchema } from '../common/id/id';
import { makeListQuery } from '../common/list-query/list-query';

import { AiCapabilitySchema } from './ai-capability/ai-capability';
import { AiFeatureKeySchema } from './ai-feature-key/ai-feature-key';
import { AiTokenUsageSchema } from './ai-token-usage';
import { AiUnavailableReasonSchema } from './ai-unavailable/ai-unavailable';

/**
 * How a call ended.
 *
 * Refusals and failures are logged as loudly as successes, because the question
 * an operator asks after an incident is "what did the platform stop doing, and
 * why" — and a log that only records what worked cannot answer it. It is also
 * what makes the budget guard auditable: the Redis counters are an accelerator
 * over exactly these rows.
 */
export const AI_CALL_STATUSES = ['ok', 'refused', 'failed'] as const;

export const AiCallStatusSchema = z.enum(AI_CALL_STATUSES);

export type AiCallStatus = (typeof AI_CALL_STATUSES)[number];

const STATUS_SET: ReadonlySet<string> = new Set<string>(AI_CALL_STATUSES);

export function isAiCallStatus(value: string): value is AiCallStatus {
  return STATUS_SET.has(value);
}

/**
 * One row of `ai.ai_usage_logs` — every AI call the platform made or refused.
 *
 * `userId` is nullable twice over: a platform-initiated call has no member, and
 * the column carries no foreign key (the `audit_logs.actorUserId` precedent), so
 * a usage record outlives the account that caused it.
 */
export const AiUsageLogSchema = z
  .object({
    id: UuidSchema,
    featureKey: AiFeatureKeySchema,
    capability: AiCapabilitySchema,
    provider: z.string(),
    model: z.string(),
    userId: UuidSchema.nullable(),
    /** A hit consumed no vendor tokens and cost nothing — the spend guards ignore it. */
    cacheHit: z.boolean(),
    status: AiCallStatusSchema,
    /** Set only when `status` is `refused`. */
    refusalReason: AiUnavailableReasonSchema.nullable(),
    /** Set only when `status` is `failed` — the vendor's error name, never its payload. */
    errorCode: z.string().nullable(),
    usage: AiTokenUsageSchema,
    costUsd: z.number().min(0),
    /**
     * Whether `costUsd` was computed from the fallback rate because the model had
     * no `ai.pricing` entry. Surfaced so an unpriced model is visible on the
     * dashboard rather than buried in a log line nobody reads.
     */
    costEstimated: z.boolean(),
    latencyMs: z.number().int().min(0),
    createdAt: z.iso.datetime(),
  })
  .strict();

export type AiUsageLog = z.infer<typeof AiUsageLogSchema>;

/** The whitelisted filters of the admin usage list (N7). */
export const AiUsageListQuerySchema = makeListQuery({
  featureKey: AiFeatureKeySchema.optional(),
  capability: AiCapabilitySchema.optional(),
  provider: z.string().min(1).max(64).optional(),
  model: z.string().min(1).max(128).optional(),
  status: AiCallStatusSchema.optional(),
  cacheHit: BooleanParamSchema.optional(),
  userId: UuidSchema.optional(),
  from: z.iso.datetime().optional(),
  to: z.iso.datetime().optional(),
});

export type AiUsageListQuery = z.infer<typeof AiUsageListQuerySchema>;
